import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { LinearProgress, makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Typography } from '@material-ui/core';
import { isPaginatedList, useQueryContext } from '@sierralabs/query-ui';
import { useSiteTablePagination } from '../../hooks/site-table/useSiteTablePagination';
import { useSiteContext } from '../../SiteContext';
// styled(TablePagination) gives an error for component='div' property
// use makeStyles instead to avoid error
const useStyles = makeStyles(() => ({
    pagination: { bottom: 0, position: 'sticky', backgroundColor: '#fff' }
}));
const StyledTable = styled(Table) `
  /* stickyHeader z-index is 2, add z-index to show ::after background color */
  &.component-hover::after {
    z-index: 3;
  }
  td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
export function formatComponent(component, plugin, column, queryValueProps) {
    var _a;
    const slotComponent = (_a = component.children) === null || _a === void 0 ? void 0 : _a.find(c => { var _a, _b; return c.type === column.componentType && ((_b = (_a = c.props) === null || _a === void 0 ? void 0 : _a.column) === null || _b === void 0 ? void 0 : _b.id) === column.id; });
    const p = Array.isArray(plugin)
        ? plugin.find(plugin => plugin.type === column.componentType)
        : plugin;
    const Component = p === null || p === void 0 ? void 0 : p.component;
    if (slotComponent && Component) {
        // pass queryIndex since queryResult.index returns last index
        return (React.createElement(Component, Object.assign({ component: slotComponent, queryValueProps: queryValueProps }, component.props)));
    }
}
const SiteTableRenderer = props => {
    var _a, _b, _c, _d;
    const { children, columns, component, isLoading, hideColumns, hidePagination, asQueryParams, workflowDisabled, queryResult, tableProps, renderHeader, onRowClick, onPaginationChange } = props;
    const classes = useStyles();
    const onPaginationChangeRef = useRef(onPaginationChange);
    onPaginationChangeRef.current = onPaginationChange;
    const { editing, plugins } = useSiteContext();
    const { iterateQuery } = useQueryContext();
    const { pagination, setPagination, isActive, handleSort, getSortDirection } = useSiteTablePagination({
        asQueryParams: asQueryParams || ((_a = component.props) === null || _a === void 0 ? void 0 : _a.sortWithQueryParams)
    });
    const colSpan = (_b = props.colSpan) !== null && _b !== void 0 ? _b : columns.length;
    const paginatedList = queryResult && isPaginatedList(queryResult.data)
        ? queryResult.data
        : undefined;
    const getColumnQueryField = (column) => {
        var _a, _b;
        // get queryField prop from format components (i.e. SiteTextPlugin, SiteDatePlugin, etc.)
        const child = (_a = component.children) === null || _a === void 0 ? void 0 : _a.find(c => { var _a, _b; return c.type === column.componentType && ((_b = (_a = c.props) === null || _a === void 0 ? void 0 : _a.column) === null || _b === void 0 ? void 0 : _b.id) === column.id; });
        return (_b = child === null || child === void 0 ? void 0 : child.props) === null || _b === void 0 ? void 0 : _b.queryField;
    };
    useEffect(() => {
        var _a;
        (_a = onPaginationChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onPaginationChangeRef, pagination);
    }, [pagination]);
    const columnsHeader = (React.createElement(React.Fragment, null, columns.map((column, index) => {
        const columnField = column.sortQueryField || getColumnQueryField(column);
        return (React.createElement(TableCell, { key: `${column.id}-${index}` }, !column.disabledSort && columnField ? (React.createElement(TableSortLabel, { active: isActive(columnField), direction: getSortDirection(columnField), onClick: () => handleSort(columnField) }, column.name)) : (column.name)));
    })));
    const table = (React.createElement(TableContainer, { className: 'site-table-container' },
        React.createElement(StyledTable, Object.assign({}, tableProps),
            React.createElement(TableHead, null,
                React.createElement(TableRow, null,
                    columns.length === 0 && (React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: 'body1', color: 'textSecondary' }, "No columns configured."))),
                    renderHeader ? renderHeader(columnsHeader) : columnsHeader)),
            React.createElement(TableBody, null,
                !hideColumns && isLoading && (React.createElement(TableRow, null,
                    React.createElement(TableCell, { colSpan: colSpan, padding: 'none' },
                        React.createElement(LinearProgress, null)))),
                !hideColumns &&
                    queryResult &&
                    iterateQuery(queryResult, (row, index) => (React.createElement(TableRow, { hover: true, key: `${row.id}-${index}`, onClick: () => {
                            if (!editing) {
                                onRowClick === null || onRowClick === void 0 ? void 0 : onRowClick(row);
                            }
                        } }, columns.map((column, colIndex) => (React.createElement(TableCell, { key: `${column.id}-${colIndex}`, padding: !column.name ? 'checkbox' : undefined, style: column.width
                            ? {
                                maxWidth: column.width
                            }
                            : undefined }, formatComponent(component, plugins, column, {
                        index,
                        workflowDisabled
                    }))))))),
                children))));
    return (React.createElement(React.Fragment, null,
        table,
        !hidePagination && (React.createElement(TablePagination, { component: 'div', className: `site-table-pagination ${classes.pagination}`, rowsPerPageOptions: [10, 25, 50, 100], count: (_c = paginatedList === null || paginatedList === void 0 ? void 0 : paginatedList.total) !== null && _c !== void 0 ? _c : 0, rowsPerPage: (_d = paginatedList === null || paginatedList === void 0 ? void 0 : paginatedList.limit) !== null && _d !== void 0 ? _d : pagination.limit, page: (paginatedList === null || paginatedList === void 0 ? void 0 : paginatedList.page) ? paginatedList.page - 1 : pagination.page, onPageChange: (_, page) => setPagination({ page }), onRowsPerPageChange: event => {
                setPagination({ page: 0, limit: +event.target.value });
            } }))));
};
export default SiteTableRenderer;
//# sourceMappingURL=SiteTableRenderer.js.map